import { IconSelectClean } from "@components/inputs/select/IconSelect";
import { ifAll, IGetValueArgs, TGetValueFn } from "@components/smart/FieldInfo";
import { SymbolConstantDef, SymbolSpecificDef, SymbolVariableDef } from "@components/smart/GeneralFieldDefinition";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntityTypeName,
    IPrDeductionEntity,
    IPrDeductionTemporalEntity,
    IPrDepositedDeductionEntity,
    PrDeductionEntity
} from "@odata/GeneratedEntityTypes";
import {
    ClearedStatusCode,
    PrChildSupportDeductionTypeCode,
    PrDeductionCalculationTypeCode,
    PrDeductionTypeCode,
    PrDistrainmentDepositClosingCode,
    PrInsolvencyDepositClosingCode
} from "@odata/GeneratedEnums";
import { getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import {
    getBankAccountFieldsDef,
    getBankAccountsFieldBaseDef,
    SAVED_ACCOUNTS_PATH
} from "@pages/banks/bankAccounts/BankAccounts.utils";
import { createDateFromPeriod } from "@pages/payroll/payroll.utils";
import { forEachKey, isDefined } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";
import Big from "big.js";
import i18next from "i18next";
import React from "react";

import { ToolbarItemType } from "../../../enums";
import { TableStorage } from "../../../model/TableStorage";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ICustomButtonDefArgs, TableActionOrder, TTableToolbarItem } from "../../../views/table/TableToolbar.utils";

export const DEDUCTION_ADD_BUTTON = "deductionAddButton";
export const ADD_EMPLOYEE_ITEM = "addEmployeeItem";
export const INSOLVENCY_DEPOSIT_CLOSING = "insolvencyDepositClosing";
export const ADD_OTHER_PAYMENT_ACTION = "addOtherPaymentAction";

export const isAmongTypes = (types: PrDeductionTypeCode[]) => (args: IGetValueArgs): boolean => {
    return types.includes(args.storage.data.entity.TypeCode);
};

export const isInsolvencyDepositClosed = (args: IGetValueArgs): boolean => {
    const entity = args.storage.data.entity as IPrDeductionEntity;
    return !!entity?.InsolvencyDepositClosingCode;
};

export const hasDepositClosingDocumentBeenLiquidated = (entity: IPrDeductionEntity): boolean => {
    const code = entity?.DepositClosingDocument?.ClearedStatusCode;
    return code === ClearedStatusCode.Cleared || code === ClearedStatusCode.PartiallyCleared;
};

export const isRegularChildSupport = (args: IGetValueArgs): boolean => {
    return args.storage.data.entity.TypeCode === PrDeductionTypeCode.ChildSupport && args.storage.data.entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Regular;
};

export const isDebtTypeOfChildSupport = (args: IGetValueArgs): boolean => {
    return args.storage.data.entity.TypeCode !== PrDeductionTypeCode.ChildSupport || args.storage.data.entity.ChildSupportType?.Code !== PrChildSupportDeductionTypeCode.Regular;
};

export const isDecreeRequiredForChildSupport = (args: IGetValueArgs): boolean => {
    const entity = args.storage.data.entity as IPrDeductionEntity;
    return entity.TypeCode === PrDeductionTypeCode.ChildSupport && (
        entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Assigned ||
        entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Substitute
    );
};

export const isCounterparty: TGetValueFn<boolean> = (args) => {
    const entity = args.storage.data.entity as IPrDeductionEntity;
    return entity.TypeCode !== PrDeductionTypeCode.Custom || !entity.IsCompanyOwn;
};

export const DEBT_BALANCE_PATH = BindingContext.localContext("DeductionBalance");
export const PAID_AMOUNT_PATH = BindingContext.localContext("PaidAmount");

type DeductionTypeCodeString = `Deduction${PrDeductionTypeCode}`

function getIconByDeductionTypeCode(typeCode: PrDeductionTypeCode): DeductionTypeCodeString {
    switch (typeCode) {
        case PrDeductionTypeCode.ChildSupport:
            return "DeductionChildSupport";
        case PrDeductionTypeCode.Custom:
            return "DeductionCustom";
        case PrDeductionTypeCode.Distrainment:
            return "DeductionDistrainment";
        case PrDeductionTypeCode.DistrainmentDeposit:
            return "DeductionDistrainmentDeposit";
        case PrDeductionTypeCode.Insolvency:
            return "DeductionInsolvency";
        case PrDeductionTypeCode.InsolvencyAdministratorReward:
            return "DeductionInsolvencyReward";
        case PrDeductionTypeCode.InsolvencyDeposit:
            return 'DeductionInsolvencyDeposit';
    }
}

export function getToolbarAddButtonConfig(storage: TableStorage, ref: React.RefObject<IconSelectClean>, args?: ICustomButtonDefArgs): TTableToolbarItem {
    const items = getEnumSelectItems(EntityTypeName.PrDeductionType).map(item => ({
        ...item,
        iconName: getIconByDeductionTypeCode(item.id as PrDeductionTypeCode),
    }));

    return {
        id: DEDUCTION_ADD_BUTTON,
        itemType: ToolbarItemType.IconSelect,
        iconName: "Add",
        label: i18next.t("Deduction:NewDeduction"),
        order: TableActionOrder.Add,
        itemProps: {
            isTransparent: true,
            selectProps: {
                isActive: storage.data.addingRow || args?.isActive,
                isDisabled: !!storage.tableAction || args?.isDisabled,
                ref
            },
            items
        }
    };
}

export function isPercentageAmountDeduction(args: IGetValueArgs): boolean {
    const { storage, bindingContext } = args;
    const deductionTemporalEntity = storage.getValue(bindingContext.getParent()) as IPrDeductionTemporalEntity;
    return deductionTemporalEntity.CalculationTypeCode !== PrDeductionCalculationTypeCode.Fixed
        && args.storage.data.entity.TypeCode !== PrDeductionTypeCode.ChildSupport && args.storage.data.entity.TypeCode !== PrDeductionTypeCode.InsolvencyAdministratorReward;
}

interface IBalanceCalculationResult {
    amountDebtTotal: number;
    paidAmount: number;
    balance: number;
}

export const calculateBalance = memoizeOne((storage: FormStorage): IBalanceCalculationResult => {
    const entity = storage.data.entity as IPrDeductionEntity;
    const origEntity = storage.data.origEntity as IPrDeductionEntity;
    let paidAmount = new Big(0), balance = new Big(0);
    const amountDebtTotal = entity.AmountDebtTotal;

    if (isDefined(amountDebtTotal) && !isNaN(amountDebtTotal)) {
        paidAmount =
            entity.PayslipDocuments?.reduce((acc, payment) => {
                return acc.add(payment.Document?.Amount ?? 0);
            }, new Big(0)) ?? new Big(0);

        origEntity.OtherPayments?.forEach(payment => {
            paidAmount = paidAmount.add(payment.Amount ?? 0);
        });

        const depositedAmount = origEntity.DepositedAmount ?? 0;

        balance = new Big(amountDebtTotal).sub(paidAmount).sub(depositedAmount);
    }

    return { amountDebtTotal, paidAmount: paidAmount.toNumber(), balance: balance.toNumber() };
}, (storage: FormStorage) => {
    const entity = storage.data.entity as IPrDeductionEntity;
    const origEntity = storage.data.origEntity as IPrDeductionEntity;
    return [entity.AmountDebtTotal, entity.PayslipDocuments, origEntity.OtherPayments];
});

export const isTransferDeposit: TGetValueFn<boolean> = (args) => {
    const entity = (args.storage as FormStorage<IPrDeductionEntity>).data.entity;
    return entity.TypeCode === PrDeductionTypeCode.DistrainmentDeposit
        ? entity.DistrainmentDepositClosingCode === PrDistrainmentDepositClosingCode.TransferToDistrainer
        : entity.InsolvencyDepositClosingCode === PrInsolvencyDepositClosingCode.TransferToInsolvencyAdministrator;
};

export function addBankAccountDefinitions(formDef: IFormDef): void {
    const bankAccountFields = getBankAccountsFieldBaseDef();
    const bankAccountDef = getBankAccountFieldsDef({ addPaymentMethod: false, areFieldsDisabled: false, type: null });

    const depositVisibility: TGetValueFn<boolean> = (args) => {
        const { entity } = (args.storage as FormStorage<IPrDeductionEntity>).data;
        const isDepositType = [PrDeductionTypeCode.DistrainmentDeposit, PrDeductionTypeCode.InsolvencyDeposit].includes(entity.TypeCode);
        return isDepositType ? isTransferDeposit(args) : true;
    };

    const isBankAccountVisible = ifAll(isCounterparty, depositVisibility);

    const fieldDefs: IFormDef["fieldDefinition"] = {
        [PrDeductionEntity.SymbolVariable]: { ...SymbolVariableDef, isVisible: isBankAccountVisible },
        [PrDeductionEntity.SymbolSpecific]: { ...SymbolSpecificDef, isVisible: isBankAccountVisible },
        [PrDeductionEntity.SymbolConstant]: { ...SymbolConstantDef, isVisible: isBankAccountVisible },
        [PrDeductionEntity.RemittanceInformation]: { isVisible: isBankAccountVisible },
        [SAVED_ACCOUNTS_PATH]: {
            ...bankAccountDef[SAVED_ACCOUNTS_PATH],
            isRequired: true,
            isVisible: isBankAccountVisible,
            collapsedRows: [
                [{ id: "BankAccount/Country" }],
                [
                    { id: "BankAccount/AccountNumber" },
                    { id: "BankAccount/Bank" },
                    { id: "BankAccount/IBAN" },
                    { id: "BankAccount/SWIFT" },
                ],
            ],
        },
    };

    forEachKey(bankAccountFields, (key) => {
        fieldDefs[createPath(PrDeductionEntity.BankAccount, key)] = {
            ...bankAccountFields[key],
            isRequired: true,
            isVisible: isBankAccountVisible,
            customizationData: {
                ...(bankAccountFields[key].customizationData ?? {}),
                useForCustomization: true
            }
        };
    });

    // Merge the new field definitions with the existing ones in the form definition
    formDef.fieldDefinition = {
        ...formDef.fieldDefinition,
        ...fieldDefs,
    };
}

export function getDateEndFromLastDeduction(depositedDeductions: IPrDepositedDeductionEntity[]): Date | null {
    if (!depositedDeductions?.length) {
        return null;
    }

    let lastDeduction: IPrDepositedDeductionEntity = null;
    depositedDeductions.forEach(d => {
        if (!lastDeduction || lastDeduction.Payslip.Year < d.Payslip.Year
            || (lastDeduction.Payslip.Year === d.Payslip.Year && lastDeduction.Payslip.Month < d.Payslip.Month)) {
            lastDeduction = d;
        }
    });

    return createDateFromPeriod(lastDeduction.Payslip, null, true);
}